/**
 * useCupoPorEntrada — front selector (compra online, eventos sin recinto).
 *
 * Same contract as the admin variant: returns a `cap(entrada)` function
 * that enforces the shared cup across multiple entradas. Each entrada can
 * still pick up to its own `units`, but the SUM across entradas is bounded
 * by the cup.
 *
 * Behaviour:
 *   - Legacy branch (cupoCompartido null/undefined): preserved byte-for-byte.
 *     `cap = min(units, n_entradas_max ?? 8)` (prevent-aware).
 *   - Stock compartido branch:
 *     `cap = min(units, max(0, cup - sumaOtras), n_entradas_max?)`.
 *     `n_entradas_max` is an INDEPENDENT, ACCUMULATIVE per-ticket-type cap
 *     that stays enforced when stock_compartido is active — activating
 *     shared stock does NOT relax the existing per-row limit. The per-row
 *     max is derived with the SAME legacy truthiness rule as the non-stock
 *     branch (falsy field — null/undefined/''/0/NaN/false — falls back to 8)
 *     so that payloads which pre-date the stock_compartido branch keep
 *     their previous cap instead of being relaxed. Front does NOT apply the
 *     admin `> 100` defensive clamp — it never did in its legacy branch.
 *   - Preventa (n_max_asientos) narrows the cap orthogonally.
 *   - FAILSAFE: stock compartido with non-finite/negative cup → returns 0
 *     and emits a debounced console.error (per instance).
 *
 * Per AGENTS.md: this composable is duplicated by context. The admin and
 * front selectors intentionally do NOT share a generic utility — they live
 * on different sides of the boundary and have different input shapes
 * (`disponibles` vs `units`).
 *
 * @param {import('vue').Ref<Array>} entradas
 * @param {import('vue').ComputedRef<number|null>|import('vue').Ref<number|null>} cupoCompartido
 * @param {import('vue').ComputedRef<number|null>|import('vue').Ref<number|null>} [preventaMax]
 * @returns {(entrada: object) => number}
 */
export function useCupoPorEntrada(entradas, cupoCompartido, preventaMax) {
    let hasLoggedDrift = false;

    return entrada => {
        // Branch off → legacy byte-for-byte.
        if (cupoCompartido.value === null || cupoCompartido.value === undefined) {
            let max = entrada.n_entradas_max ? entrada.n_entradas_max : 8;

            if (preventaMax && preventaMax.value !== null && preventaMax.value !== undefined) {
                max = preventaMax.value;
            }

            return entrada.units > max ? max : entrada.units;
        }

        const cup = Number(cupoCompartido.value);

        if (!Number.isFinite(cup) || cup < 0) {
            if (process.env.NODE_ENV !== 'production' && !hasLoggedDrift) {
                console.error('[useCupoPorEntrada] stock_compartido=true sin cupo_disponible válido — payload drift');
                hasLoggedDrift = true;
            }
            return 0;
        }

        // Sum the units already selected in OTHER entradas (NOT the current one).
        const sumaOtras = entradas.value.reduce((acc, e) => {
            if (e === entrada) {
                return acc;
            }
            return acc + (Number(e.unidades) || 0);
        }, 0);

        const restante = Math.max(0, cup - sumaOtras);
        let base = entrada.units > restante ? restante : entrada.units;

        // n_entradas_max is an independent, accumulative per-row cap that must
        // remain enforced even when stock_compartido is active. Shared stock
        // does not relax the existing per-ticket-type purchase limit — it only
        // adds a SUM constraint on top of it. Derive the legacy max first
        // using the SAME truthiness rule as the non-stock branch
        // (falsy field → fallback to 8). `Number(null)` and `Number('')` are 0
        // (not NaN), so doing the conversion first would clamp the cap to 0
        // and mark the entrada as AGOTADA. `Number(undefined)` is NaN, so
        // doing the conversion first would skip the leg and relax the
        // previous cap from 8 to whatever the cup allows. The truthiness
        // step is the legacy byte-compat anchor. Front does NOT apply the
        // admin `> 100` defensive clamp — it never did in its legacy branch.
        const nMaxRaw = entrada.n_entradas_max ? entrada.n_entradas_max : 8;
        let nMax = Number(nMaxRaw);

        if (!Number.isFinite(nMax) || nMax < 0) {
            nMax = 8;
        }

        if (base > nMax) {
            base = nMax;
        }

        // Preventa narrows the cap orthogonally.
        if (preventaMax && preventaMax.value !== null && preventaMax.value !== undefined) {
            const preventa = Number(preventaMax.value);

            if (Number.isFinite(preventa) && preventa >= 0 && preventa < base) {
                base = preventa;
            }
        }

        return base;
    };
}